/**
 * #1292 — the per-MESSAGE inline budget.
 *
 * The composer inlines a file (base64 into the send body) when it is under
 * `MAX_INLINE_SIZE` (10 MB). That was a per-FILE limit only, so two 8 MB photos
 * made a 22 MB send. The send body is capped at 16 MB on the server — 10 MB of
 * attachments after base64 — so the same 10 MB is now also the budget for the
 * whole message: a file that would take the message's inline total past it is
 * UPLOADED instead (the session-attachment upload larger files already use).
 * For an image or PDF that changes nothing the AI sees: an uploaded one is
 * resolved back to its bytes at the same 10 MB ceiling (`convert-messages.ts`).
 *
 * DECIDED ONCE, AT ATTACH TIME (owner, #1292 comment): the route is fixed when
 * the file is attached and never changes afterwards — removing an inlined file
 * does not pull an earlier overflow back to inline. And it is RESERVED then, in
 * the same synchronous step: a paste, a drop and the file picker that land
 * together each see what the others took, so they can't all fit into the same
 * remaining budget.
 *
 * WHAT COUNTS. The committed composer list is the truth — an inline chip counts
 * while it is uploading or holds `data:` bytes, so removing it, sending it, a
 * failed read or a move to the drive gives its bytes back with no bookkeeping.
 * The only extra state is the gap between "reserved" and "React has committed
 * the chip": a pending entry, dropped as soon as the chip is seen committed (or,
 * if it never shows up, after {@link PENDING_TTL_MS}).
 */
import { MAX_MODEL_MEDIA_BYTES } from '@papan/types'

/** The whole message's inline attachments — the same 10 MB as the per-file
 *  limit, so users see one number. The server's 16 MB body cap is this after
 *  base64, plus the message text (`apps/api/src/body-limits.ts`). */
export const MAX_INLINE_MESSAGE_BYTES = MAX_MODEL_MEDIA_BYTES

const PENDING_TTL_MS = 30_000

/** The fields of a composer attachment the budget reads. */
export interface BudgetedAttachment {
  id: string
  status: 'uploading' | 'ready' | 'error'
  url?: string
  /** #662's legacy stash of the original inline bytes on a moved chip. The send
   *  still carries it (hooks.ts maps `previewUrl` into the body), so it counts. */
  previewUrl?: string
  /** Raw bytes this attachment inlines (set at attach time on the inline route). */
  inlineBytes?: number
}

/** Decoded size of a base64 `data:` URL, from its length (no decode). */
function dataUrlBytes(url: string): number {
  const comma = url.indexOf(',')
  if (comma < 0) return 0
  const payload = url.length - comma - 1
  return url.slice(5, comma).includes('base64') ? Math.floor((payload * 3) / 4) : payload
}

/** Bytes a committed attachment still holds against the message's budget.
 *  A chip with `data:` bytes but no `inlineBytes` — one rebuilt from a sent
 *  message for an edit-resend — is measured from its URL, or a 10 MB original
 *  plus new files would pass the budget and the resend would be refused. */
export function inlineBytesOf(a: BudgetedAttachment): number {
  if (a.status === 'error') return 0
  if (a.status === 'uploading') return a.inlineBytes ?? 0
  // A moved chip rebuilt for an edit can still hold its old bytes in `previewUrl`
  // (the local message copy keeps them until a reload) — they are sent, so count.
  const stash = a.previewUrl?.startsWith('data:') ? dataUrlBytes(a.previewUrl) : 0
  if (!a.url?.startsWith('data:')) return stash
  return (a.inlineBytes ?? dataUrlBytes(a.url)) + stash
}

export interface InlineBudget {
  /** Reserve `bytes` for attachment `id` in the composer `key`, given that
   *  composer's committed attachments. `true` = it fits (inline it); `false` =
   *  it would pass the budget (upload it instead). Synchronous by design. */
  reserve(key: string, id: string, bytes: number, committed: readonly BudgetedAttachment[]): boolean
  /** Drop pending entries React has committed (or that never arrived). Call
   *  after every commit of the composer lists, with every committed id. */
  settle(committedIds: ReadonlySet<string>): void
  /** Forget one reservation now (its file failed before it was added). */
  release(id: string): void
  /** A draft became a session: its pending reservations move with its chips. */
  rekey(from: string, to: string): void
}

export function createInlineBudget(limit: number = MAX_INLINE_MESSAGE_BYTES, now: () => number = Date.now): InlineBudget {
  const pending = new Map<string, { key: string; bytes: number; at: number }>()
  return {
    reserve(key, id, bytes, committed) {
      const committedIds = new Set(committed.map((a) => a.id))
      let used = 0
      for (const a of committed) used += inlineBytesOf(a)
      for (const [pid, p] of pending) if (p.key === key && !committedIds.has(pid)) used += p.bytes
      if (used + bytes > limit) return false
      pending.set(id, { key, bytes, at: now() })
      return true
    },
    settle(committedIds) {
      const t = now()
      for (const [id, p] of pending) {
        if (committedIds.has(id) || t - p.at > PENDING_TTL_MS) pending.delete(id)
      }
    },
    release(id) {
      pending.delete(id)
    },
    rekey(from, to) {
      for (const p of pending.values()) if (p.key === from) p.key = to
    },
  }
}
